// Centralized API Service for File In Canada

const API_BASE_URL = 'https://leadapi.crmbytes.com/api';
const GEOLOCATION_API = 'https://validationapi.thetechmenders.com/api/GeoLocation/GetGeoLocation';

/**
 * Fetch user's geolocation data
 */
export const getGeoLocation = async () => {
  try {
    const response = await fetch(GEOLOCATION_API, {
      method: 'GET',
      headers: {
        'accept': 'text/plain'
      }
    });

    if (!response.ok) {
      throw new Error('Failed to fetch geolocation');
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Geolocation API Error:', error);
    return null;
  }
};

/**
 * Extract country code from dial code
 * e.g., "+1" -> "US", "+44" -> "GB", etc.
 */
const getCountryCodeFromDialCode = (dialCode) => {
  const countryMap = {
    '+1': 'US',
    '+44': 'GB',
    '+91': 'IN',
    '+86': 'CN',
    '+81': 'JP',
    '+82': 'KR',
    '+61': 'AU',
    '+49': 'DE',
    '+33': 'FR',
    '+39': 'IT',
    '+34': 'ES',
    '+7': 'RU',
    '+55': 'BR',
    '+52': 'MX',
    '+27': 'ZA',
    '+971': 'AE',
    '+65': 'SG',
    '+31': 'NL',
    '+46': 'SE'
  };
  
  return countryMap[dialCode] || 'US';
};

/**
 * Submit lead form data to CRM
 */
export const submitLead = async (formData, geoLocationData = null) => {
  try {
    // Clean phone number - remove all non-numeric characters
    const cleanPhone = formData.phone.replace(/\D/g, '');
    
    // Extract dial code number (remove +)
    const countryDialCode = parseInt(formData.countryCode.replace('+', ''));
    
    // Get country code from dial code or use geolocation
    let countryCode = getCountryCodeFromDialCode(formData.countryCode);
    
    // If we have geolocation data, use it
    if (geoLocationData?.isSuccess) {
      countryCode = geoLocationData.data.countryCode;
    }

    // Build payload
    const payload = {
      name: formData.name,
      email: formData.email,
      countryDialCode: countryDialCode,
      country: countryCode,
      phoneNumber: cleanPhone, // Only numeric
      ipAddress: geoLocationData?.data?.ipAddress || '',
      description: formData.message || formData.description || 'Trademark registration service inquiry',
      pageUrl: window.location.href,
      city: geoLocationData?.data?.cityName || '',
      state: geoLocationData?.data?.regionName || '',
      geoLocation: geoLocationData ? JSON.stringify(geoLocationData) : '',
      packageTitle: formData.packageTitle || '',
      packageAmount: formData.packageAmount || 0
    };

    console.log('Submitting lead with payload:', payload);

    const response = await fetch(`${API_BASE_URL}/Lead/AddLead`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify(payload)
    });

    const data = await response.json();

    // Handle different response scenarios
    if (response.status === 200 && data.isSuccess) {
      // Success - redirect to application page
      const customerId = data.data.customerId;
      window.location.href = `/application/main?customerid=${customerId}`;
      return {
        success: true,
        data: data
      };
    } else if (response.status === 401) {
      // Unauthorized
      return {
        success: false,
        error: data.message || 'Unauthorized access'
      };
    } else if (response.status === 400) {
      // Bad Request
      return {
        success: false,
        error: data.errorMessage || data.data?.message || 'Invalid data provided'
      };
    } else {
      // Other errors
      return {
        success: false,
        error: data.errorMessage || 'An error occurred. Please try again.'
      };
    }
  } catch (error) {
    console.error('Lead submission error:', error);
    return {
      success: false,
      error: 'Network error. Please check your connection and try again.'
    };
  }
};

/**
 * Complete form submission flow:
 * 1. Get geolocation
 * 2. Submit lead with geolocation data
 */
export const submitFormWithGeolocation = async (formData) => {
  // First, get geolocation
  const geoData = await getGeoLocation();
  
  // Then submit the lead with geolocation data
  return await submitLead(formData, geoData);
};
